import React, { useState, useEffect, useMemo } from "react";
import { C, T } from "../constants/baziData";
import { calcShishen, calcDayun, analyzeLiunian } from "../utils/baziCalc";
import { API } from "../constants/config";
import FeedbackSection from "../components/FeedbackSection";
import PersonalizedInsightPanel from "../components/PersonalizedInsightPanel";
import EnergyCrossAnalysis from "../components/EnergyCrossAnalysis";
import ShishenCards from "../components/ShishenCards";
import NayinShenshaSection from "../components/NayinShenshaSection";
import MonthlyTimeline from "../components/MonthlyTimeline";
import LayerAnalysis from "../components/LayerAnalysis";
import YearlyProtocol from "../components/YearlyProtocol";
import ZiweiPalaces from "../components/ZiweiPalaces";
import CoverPage from "../components/CoverPage";
import PillarsPage from "../components/PillarsPage";
import IdentityPage from "../components/IdentityPage";
import DomainCards from "../components/DomainCards";
import CyclesPage from "../components/CyclesPage";
import HarmonyPage from "../components/HarmonyPage";
import BackCover from "../components/BackCover";
import EnergyMatchCard from "../components/EnergyMatchCard";

// ── Report view ───────────────────────────────────────────────────────────────
function ReportView({lang="en",result,onReset,birthYear,API,setPage,user}){
  const {pillars,sc,name,location,ts,rid}=result;
  const shishen=calcShishen(pillars,lang);
  const dayun=calcDayun(pillars,birthYear||1980,result.gender||"M",lang);
  const liunian=analyzeLiunian(pillars,lang);
  const [pdfLoad,setPdfLoad]=useState(false);
  const [copied,setCopied]=useState(false);
  const [activeAnchor,setActiveAnchor]=useState("bazi");
  const [printMode,setPrintMode]=useState(false);
  // Phase 35 Round 2: Pro PDF + 澄光 ChatDrawer
  const [proLoad,setProLoad]=useState(false);
  const [chatOpen,setChatOpen]=useState(false);
  const [chatMessages,setChatMessages]=useState([]);
  const [chatInput,setChatInput]=useState("");
  const [chatLoad,setChatLoad]=useState(false);
  const [chatSessionId,setChatSessionId]=useState(null);
  const [chatErr,setChatErr]=useState("");
  const [sections,setSections]=useState([]);

  // Safe _form: use result._form if present, otherwise reconstruct from pillars
  const _form = result._form || (() => {
    const p = pillars || {};
    const yr = p.year  ? (p.year.year  || birthYear || 1978) : (birthYear || 1978);
    const mo = p.month ? (p.month.month || 7)  : 7;
    const dy = p.day   ? (p.day.day    || 11)  : 11;
    const hr = p.hour  ? (p.hour.hour  || 13)  : 13;
    return { year:yr, month:mo, day:dy, hour:hr, minute:0,
             name: result.name||"", gender:"M", location:"", concern:"", questions:[] };
  })();

  // ── Active anchor via IntersectionObserver ────────────────────────────────
  // Scroll to top on mount
  useEffect(()=>{ window.scrollTo(0,0); },[]);

  useEffect(()=>{
    const secs=["bazi","deity","monthly","dayun","solutions"];
    const obs=new IntersectionObserver(entries=>{
      entries.forEach(e=>{
        if(e.isIntersecting) setActiveAnchor(e.target.id.replace("sec-",""));
      });
    },{rootMargin:"-40% 0px -40% 0px"});
    secs.forEach(id=>{const el=document.getElementById(`sec-${id}`);if(el)obs.observe(el);});
    return()=>obs.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(()=>{
    if(!API) return;
    fetch(`${API}/report/sections`)
      .then(r=>r.json())
      .then(d=>setSections(d.sections||[]))
      .catch(()=>setSections([]));
  },[API]);

  // ── Copy key points to clipboard ─────────────────────────────────────────
  const copyKeyPoints=()=>{
    const deity=sc.proto?.deity||"";
    const dominant=sc.dominant||"";
    const lines=[
      lang==="zh"?"【澄光能量审计报告摘要】":"[LUMINAARC Energy Audit Summary]",
      `${lang==="zh"?"姓名":"Name"}: ${name}`,
      `${lang==="zh"?"地点":"Location"}: ${location||"—"}`,
      `${lang==="zh"?"报告编号":"Report ID"}: ${rid}`,
      "",
      `${lang==="zh"?"综合能量评分":"Overall Score"}: ${sc.overall}/100`,
      `${lang==="zh"?"主导元素":"Dominant Element"}: ${dominant}`,
      ...(sc.lacking?[`${lang==="zh"?"不足元素":"Deficient Element"}: ${sc.lacking}`]:[]),
      `${lang==="zh"?"推荐本尊":"Deity Rec."}: ${deity}`,
      "",
      `${lang==="zh"?"活力":"Vitality"}: ${sc.vitality}  ${lang==="zh"?"压力":"Stress"}: ${sc.stress}  ${lang==="zh"?"动势":"Momentum"}: ${sc.momentum}`,
      "",
      "lumarcai.com",
    ];
    navigator.clipboard.writeText(lines.join("\n")).then(()=>{
      setCopied(true);
      setTimeout(()=>setCopied(false),2200);
    });
  };

  const downloadPdf = async () => {
    if(!API) return;
    setPdfLoad(true);
    try {
      const token = localStorage.getItem("lumina_token");
      const res = await fetch(`${API}/audit/full-report/pdf`, {
        method: "POST",
        headers: {"Authorization":`Bearer ${token}`,"Content-Type":"application/json"},
        body: JSON.stringify({..._form, minute: +(_form.minute??0)}),
      });
      if(!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const {year,month,day} = _form;
      const filename = `LUMINAARC_Report_${year}${String(month).padStart(2,"0")}${String(day).padStart(2,"0")}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch(e) {
      alert(lang==="zh"?`PDF 下载失败: ${e.message}`:`PDF download failed: ${e.message}`);
    } finally {
      setPdfLoad(false);
    }
  };

  // ── Phase 35: Pro PDF (澄光能量完整报告) ─────────────────────────────────
  const downloadProPdf = async () => {
    setProLoad(true);
    try {
      const token = localStorage.getItem("lumina_token");
      const res = await fetch(`${API}/audit/full-report-pro`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          birth_year:   +_form.year,
          birth_month:  +_form.month,
          birth_day:    +_form.day,
          birth_hour:   +_form.hour,
          birth_minute: +(_form.minute ?? 0),
          gender:       _form.gender === "F" ? "female" : "male",
          name:         _form.name || "",
          language:     lang || "zh",
          include_ziwei: true,
          questions:    (_form.questions || []).filter(Boolean),
        }),
      });
      if (!res.ok) throw new Error(`服务器错误 ${res.status}`);
      const blob = await res.blob();
      const filename = `CG-${birthY}${String(birthM).padStart(2,"0")}${String(birthD).padStart(2,"0")}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(lang === "zh" ? `PDF 下载失败: ${e.message}` : `PDF download failed: ${e.message}`);
    } finally {
      setProLoad(false);
    }
  };

  // ── Phase 35: 向澄光提问 — POST /rag/chat (context_type=bazi) ────────────
  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoad || !API) return;
    const token = localStorage.getItem("lumina_token");
    if (!token) {
      alert(lang==="zh"?"请先登录后再使用 AI 追问":"Please log in to use AI follow-up");
      return;
    }
    setChatErr("");
    setChatLoad(true);
    setChatMessages(m => [...m, {role:"user", content:msg}]);
    setChatInput("");
    try {
      const res = await fetch(`${API}/rag/chat`, {
        method: "POST",
        headers: {"Authorization":`Bearer ${token}`,"Content-Type":"application/json"},
        body: JSON.stringify({
          message: msg,
          session_id: chatSessionId,
          include_chart: true,
          context_type: "bazi",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) {
        const detail = data.detail || (lang==="zh"
          ? "已用完免费额度。L1 订阅或购买 $149 30 天访问可继续。"
          : "Free quota used. L1 subscription or $149 30-day access required.");
        setChatErr(detail);
        setChatMessages(m => [...m, {role:"assistant", content:detail, isError:true}]);
        return;
      }
      if (!res.ok) {
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      if (data.session_id) setChatSessionId(data.session_id);
      const reply = data.message || data.assistant || data.response || "";
      setChatMessages(m => [...m, {role:"assistant", content:reply || "（无回复）"}]);
    } catch (e) {
      const errMsg = lang==="zh"?`追问失败：${e.message}`:`Follow-up failed: ${e.message}`;
      setChatErr(errMsg);
      setChatMessages(m => [...m, {role:"assistant", content:errMsg, isError:true}]);
    } finally {
      setChatLoad(false);
    }
  };

  // Quota label for chat drawer header
  const chatQuotaLabel = (() => {
    if (!user) return lang==="zh"?"请先登录":"Login required";
    const tier = user.tier;
    const expires = user.chat_access_expires;
    if (expires) {
      const days = Math.max(0, Math.ceil((new Date(expires) - new Date()) / 86400000));
      return lang==="zh"
        ? `30 天无限追问 · 剩余 ${days} 天`
        : `Unlimited 30-day window · ${days} days left`;
    }
    if (tier === "pro") return lang==="zh"?"无限追问":"Unlimited";
    if (tier === "free") return lang==="zh"?"本月剩余 ~10 次":"~10 chats / month";
    return lang==="zh"?"免费体验":"Free trial";
  })();

  // ── Chart data source router ──────────────────────────────────────────────
  // Backend v3 report (when available) provides real dayun/domains arrays.
  // Local path falls back to adapter logic from stage 3 integration.
  const chartData = useMemo(() => {
    const backend = result?._backend;
    const isBackend = result?._source === "backend" && backend;

    if (isBackend) {
      // Use backend's native shapes — they match chart components directly.
      return {
        elements: backend.elements?.percentages || {},
        dayunList: backend.dayun?.list || [],
        domains:   backend.domains || [],
      };
    }

    // Local fallback — existing adapter logic, unchanged.
    const elements = sc?.pct || {};
    const dayunList = (dayun || []).map(d => ({
      order:       d.order,
      ganzhi:      d.gz,
      start_age:   d.startAge,
      end_age:     d.endAge,
      stem:        d.stem,
      branch:      d.branch,
      quality:     d.quality,
      description: d.desc,
    }));
    const baseScore = sc?.overall ?? 50;
    const pct = sc?.pct || {};
    const leanWealth = Math.round(baseScore + (pct.Earth ?? pct["土"] ?? 0) * 0.3 - 10);
    const leanCareer = Math.round(baseScore + (pct.Metal ?? pct["金"] ?? 0) * 0.3 - 5);
    const leanRel    = Math.round(baseScore + (pct.Water ?? pct["水"] ?? 0) * 0.4 - 8);
    const leanHealth = Math.round(baseScore + (pct.Wood  ?? pct["木"] ?? 0) * 0.3 - 6);
    const clamp = v => Math.max(30, Math.min(95, v));
    const domains = sc?.domains || [
      { domain: "财运", domain_en: "Wealth",        score: clamp(leanWealth) },
      { domain: "事业", domain_en: "Career",        score: clamp(leanCareer) },
      { domain: "感情", domain_en: "Relationships", score: clamp(leanRel) },
      { domain: "健康", domain_en: "Health",        score: clamp(leanHealth) },
    ];
    return { elements, dayunList, domains };
  }, [result, sc, dayun]);

  const pColors=["#7a6a40","#3a3020","#3a4a2a","#2a3040"];
  const domains=[
    {domain:T[lang].wealth,score:Math.min(100,Math.round(sc.overall*0.9+10)),
      summary:lang==="zh"?"基于命局中的财星和官星分析":"Based on financial and career stars in your chart",
      advice:lang==="zh"?"善用日主优势元素创造财富":"Leverage your dominant element for wealth creation",color:C.gold},
    {domain:T[lang].love,score:sc.relational,
      summary:lang==="zh"?"土元素主导您的人际关系场":"Earth element governs your relational field",
      advice:lang==="zh"?"增强土元素以深化感情连结":"Strengthen Earth element to deepen connections",color:C.fire},
    {domain:T[lang].health,score:sc.vitality,
      summary:lang==="zh"?"木火元素驱动生命力能量":"Wood and Fire elements drive life force energy",
      advice:lang==="zh"?`通过饮食和修持调节${{"Fire":"火","Wood":"木","Water":"水","Earth":"土","Metal":"金"}[sc.dominant]||sc.dominant}过旺`:`Address ${sc.dominant} excess through diet and practice`,color:C.sage},
    {domain:T[lang].spiritual,score:Math.min(100,Math.round(sc.overall*0.85+15)),
      summary:lang==="zh"?"金水元素支持内在发展与灵性修持":"Metal and Water elements support inner development",
      advice:lang==="zh"?"规律修持能调整您的能量场":"Regular practice aligns your energy field",color:C.cyan},
  ];

  // Accuracy metadata: prefer backend field, fall back to tier-based local defaults
  const accuracyMeta = result._backend?.accuracy || (
    result._source === "backend"
      ? null
      : { accuracy:70, label_en:"Basic", label_zh:"\u57fa\u7840\u7248",
          description_en:"Local calculation using traditional Bazi formulas",
          description_zh:"\u672c\u5730\u8ba1\u7b97\uff0c\u57fa\u4e8e\u4f20\u7edf\u516b\u5b57\u516c\u5f0f" }
  );
  const tierKey = result._backend?.tier_key || (result._source==="backend" ? "L1" : "L0");

  const ANCHORS = lang==="zh"
    ? [["bazi","四柱"],["deity","本尊"],["monthly","月运"],["dayun","大运"],["solutions","解决方案"]]
    : [["bazi","Pillars"],["deity","Deity"],["monthly","Monthly"],["dayun","Cycles"],["solutions","Solutions"]];

  // ── Phase 36: parchment-gold v2 data prep ────────────────────────────────
  const _backend = result?._backend || {};
  const _narratives = _backend?._narratives || {};
  const STEM_ELEM = {甲:"wood",乙:"wood",丙:"fire",丁:"fire",戊:"earth",己:"earth",庚:"metal",辛:"metal",壬:"water",癸:"water"};
  const pElem = (gz) => STEM_ELEM[gz?.[0]] || "earth";
  const dayStem = pillars?.day?.gz?.[0] || "";
  const dayElemZh = ({wood:"木",fire:"火",earth:"土",metal:"金",water:"水"})[STEM_ELEM[dayStem]] || "";
  const ELEM_LABEL = {wood:"木",fire:"火",earth:"土",metal:"金",water:"水"};
  const ELEM_LABEL_EN = {wood:"Wood",fire:"Fire",earth:"Earth",metal:"Metal",water:"Water"};

  // Element percentages — Pro engine returns raw counts in elements_counts; normalize to %
  const _ec = _backend?.elements_counts || {};
  const _ecSum = Object.values(_ec).reduce((a,b)=>a+b, 0);
  const _pctMap = _ecSum > 0
    ? {
        wood:  ((_ec["木"] ?? _ec["Wood"] ?? 0) / _ecSum * 100),
        fire:  ((_ec["火"] ?? _ec["Fire"] ?? 0) / _ecSum * 100),
        earth: ((_ec["土"] ?? _ec["Earth"] ?? 0) / _ecSum * 100),
        metal: ((_ec["金"] ?? _ec["Metal"] ?? 0) / _ecSum * 100),
        water: ((_ec["水"] ?? _ec["Water"] ?? 0) / _ecSum * 100),
      }
    : {
        wood:  sc?.pct?.Wood  ?? 0,
        fire:  sc?.pct?.Fire  ?? 0,
        earth: sc?.pct?.Earth ?? 0,
        metal: sc?.pct?.Metal ?? 0,
        water: sc?.pct?.Water ?? 0,
      };
  const _maxPct = Math.max(..._pctMap?Object.values(_pctMap):[0]);
  const isOver = (p) => p >= 40;
  const isUnder = (p) => p > 0 && p < 8;

  // Strength / xiyong fallbacks
  const strengthScore = _backend?.strength_score ?? _backend?.strength?.strength_score ?? 50;
  const strengthLabel = _backend?.strength_label ?? _backend?.strength?.category ?? (lang==="zh"?"中和":"Balanced");
  const xy = _backend?.xiyong || _backend?.xiyongshen || {};
  const yongElem  = xy.yong?.element || xy.yong_shen || "";
  const xiElem    = xy.xi?.element   || xy.xi_shen   || "";
  const jiElem    = xy.ji?.element   || xy.ji_shen   || "";
  const chouElem  = xy.chou?.element || xy.chou_shen || "";
  const yongDesc  = xy.yong?.desc || (lang==="zh"?"增益最直接的能量":"most needed energy");
  const xiDesc    = xy.xi?.desc   || (lang==="zh"?"扩展能量边界":"reinforces alignment");
  const jiDesc    = xy.ji?.desc   || (lang==="zh"?"已过旺·继续增加会加重内耗":"already excess, avoid stacking");
  const chouDesc  = xy.chou?.desc || (lang==="zh"?"高约束环境消耗能量":"high-friction environments drain energy");
  const ELEM_EMOJI = {"水":"💧","木":"🌿","火":"🔥","土":"🏔","金":"⚔"};

  // Kongwang
  const kw = _backend?.kongwang || {};
  const kwBranches = kw?.kongwang_branches || [];
  const kwRotation = kw?.rotation || (pillars?.day?.gz ? `${pillars.day.gz}旬` : "");
  const kwHasData = kwBranches.length > 0;

  // Birth line
  const _formData = result?._form || {};
  const birthY = _formData.year || birthYear || "";
  const birthM = _formData.month || "";
  const birthD = _formData.day || "";
  const birthH = _formData.hour ?? "";
  const _hourKnown = +birthH >= 0;  // Phase 84: -1 = unknown
  const _genderRaw = _formData.gender || result?.gender || "M";
  const genderLabel = _genderRaw === "F" || _genderRaw === "female"
    ? (lang==="zh"?"女命":"Female")
    : (lang==="zh"?"男命":"Male");
  const _hourBranch = (() => {
    const h = +birthH;
    if (isNaN(h) || h < 0) return "";
    const branches = ["子","丑","寅","卯","辰","巳","午","未","申","酉","戌","亥"];
    const idx = (h === 23 || h === 0) ? 0 : Math.floor((h + 1) / 2);
    return branches[idx % 12];
  })();
  const birthLine = (birthY && birthM && birthD)
    ? (_hourKnown
        ? `${birthY}年${String(birthM).padStart(2,"0")}月${String(birthD).padStart(2,"0")}日 · ${_hourBranch}时（${String(birthH).padStart(2,"0")}:00）生 · ${genderLabel}`
        : `${birthY}年${String(birthM).padStart(2,"0")}月${String(birthD).padStart(2,"0")}日 · ${genderLabel} · ⚠️时辰未提供（六字模式）`)
    : "";

  // Report number
  const reportNumber = _backend?.report_number || result?.rid || `LMN-${birthY}${String(birthM).padStart(2,"0")}${String(birthD).padStart(2,"0")}`;
  const genDate = result?.ts ? new Date(result.ts).toISOString().slice(0,10) : new Date().toISOString().slice(0,10);

  // Domain data with fallbacks
  const _backendDomains = _backend?.domains || [];
  const domainStars = (s) => {
    const n = Math.max(1, Math.min(5, Math.round((s||50)/20)));
    return "★".repeat(n) + "☆".repeat(5-n);
  };
  const v2Domains = (lang==="zh"
    ? [["💼","事业与财富","career"],["❤","感情与婚姻","relationships"],["🏡","家庭关系","family"],["🌿","健康与身体","health"]]
    : [["💼","Career & Wealth","career"],["❤","Relationships","relationships"],["🏡","Family","family"],["🌿","Health","health"]]
  ).map(([emoji, name, key], i) => {
    const bd = _backendDomains[i] || {};
    // v2→v1 fallback mapping: v1 array is [wealth, love, health, spiritual]
    const v1FallbackIdx = {career:0, relationships:1, family:-1, health:2}[key];
    const v1Domain = (v1FallbackIdx >= 0) ? domains[v1FallbackIdx] : null;
    return {
      emoji, name,
      score: bd.score ?? v1Domain?.score ?? 60,
      narrative: bd.narrative || v1Domain?.summary || (lang==="zh"?"基于命局五行与十神综合分析。":"Based on the chart's five-element & deity profile."),
      key: bd.key_insight || bd.year_advice || v1Domain?.advice || "",
    };
  });

  // Dayun list (use chartData.dayunList from existing memoization)
  const _dyList = chartData?.dayunList || [];
  const _curIdx = (() => {
    const age = (new Date().getFullYear()) - (birthY || 1980);
    return _dyList.findIndex(d => age >= d.start_age && age <= d.end_age);
  })();
  const dayunSlice = _curIdx >= 0
    ? _dyList.slice(Math.max(0, _curIdx), Math.min(_dyList.length, _curIdx + 3))
    : _dyList.slice(0, 3);

  // 3-year liunian fallback
  const liunian3 = _backend?.liunian?.next_3_years || (() => {
    const startY = new Date().getFullYear();
    return [0,1,2].map(i => ({
      year: startY+i,
      ganzhi: "—",
      theme: lang==="zh"?"流年走势":"Annual outlook",
      score: 6.5 + (i % 3 === 0 ? 0.7 : -0.2),
    }));
  })();

  // Action guidance (3 items)
  const actionGuidance = _backend?.action_guidance || _backend?.current_period?.action_guidance || [];

  // Questions / Q&A
  const questions = _formData.questions || [];
  const qaAnswers = _backend?.qa_answers || [];
  const hasQuestions = questions.length > 0 || qaAnswers.length > 0;

  // Harmonization
  const harm = _backend?.harmonization || {};
  const _yongElemZh = yongElem || (lang==="zh"?"水":"Water");
  const harmDirections = harm.directions || [
    {key:"吉方", text:`${_yongElemZh}方为佳`},
    {key:"居所", text:lang==="zh"?"靠近自然元素的环境为宜":"Near natural elements"},
    {key:"办公", text:lang==="zh"?"背靠实墙·面向用神方位":"Solid wall behind · facing favorable direction"},
  ];
  const harmColors = harm.colors || [
    {key:"增益色", text:lang==="zh"?`${_yongElemZh}系深色`:`Deep ${_yongElemZh.toLowerCase()} tones`},
    {key:"避免", text:lang==="zh"?"忌神元素的强烈饱和色":"Saturated colors of the unfavorable element"},
    {key:"材质", text:lang==="zh"?"自然材质·天然木材·绿植":"Natural materials · live plants"},
  ];
  const _rptAge = (new Date().getFullYear()) - (birthY || 1980);
  const _isJuvenile = _rptAge < 22;
  const _industryField = _isJuvenile && xy.favorable_academic
    ? xy.favorable_academic
    : (xy.favorable_industry || (lang==="zh"?"教育·咨询·文化":"Education · consulting · culture"));
  const harmIndustries = harm.industries || [
    {key:_isJuvenile?"专业方向":"最宜", text:_industryField},
    {key:_isJuvenile?"探索方向":"次宜", text:lang==="zh"?"自然疗愈·跨文化连接":"Natural healing · cross-cultural"},
    {key:"避免", text:lang==="zh"?"长期处于忌神元素强势的环境":"Avoid prolonged unfavorable-element environments"},
  ];
  const harmRhythm = harm.rhythm || [
    {key:"每周", text:lang==="zh"?"至少一次接触用神元素活动":"Weekly contact with the favorable element"},
    {key:"每日", text:lang==="zh"?"10-20 分钟独处时间":"10-20 min solitude daily"},
    {key:"饮食", text:lang==="zh"?"用神元素对应的食物":"Foods aligned with the favorable element"},
    {key:"避免", text:lang==="zh"?"连续无松弛期超过 6 周":"No more than 6 weeks without a real break"},
  ];

  // Vajra credentials (from user prop)
  const userCredentials = user?.credentials || [];

  return(
    <div className="lr2-shell">
      <style>{`
        .lr2-shell {
          background: #DDD5C0; position: relative;
          min-height: 100vh;
          padding: 48px 0 48px;
          font-family: 'Noto Serif SC', 'Source Han Serif SC', 'PingFang SC', 'SimSun', Georgia, serif;
          color: #333;
        }
        .lr2-nav {
          background: #1A1410; color: rgba(255,255,255,.55);
          font-family: -apple-system, 'Inter', sans-serif; font-size: 11px;
          padding: 10px 24px; display: flex; gap: 18px; align-items: center;
          position: fixed; top: 60px; left: 0; right: 0; z-index: 101; flex-wrap: wrap;
        }
        .lr2-nav-brand { color: #F5C842; font-weight: 600; }
        .lr2-nav a {
          color: rgba(255,255,255,.55); text-decoration: none;
          padding: 2px 0; border-bottom: 1px solid transparent; transition: .2s; cursor: pointer;
        }
        .lr2-nav a:hover, .lr2-nav a.active { color: #F5C842; border-bottom-color: #F5C842; }
        .lr2-nav-badge {
          margin-left: auto; background: rgba(245,200,66,.12);
          border: 1px solid rgba(245,200,66,.3); color: #F5C842;
          padding: 2px 10px; border-radius: 10px; font-size: 10px; letter-spacing: .08em;
        }
        .lr2-report {
          max-width: 860px; margin: 28px auto; background: #FDFBF7;
          box-shadow: 0 12px 60px rgba(0,0,0,.22); border-radius: 3px; overflow: hidden;
        }
        /* Cover */
        .lr2-cover {
          background: #846B32; position: relative; overflow: hidden;
          min-height: 500px; display: flex; flex-direction: column;
        }
        .lr2-cover-bg {
          position: absolute; inset: 0;
          background:
            radial-gradient(ellipse 120% 80% at 50% 0%, rgba(245,200,66,.18) 0%, transparent 60%),
            radial-gradient(ellipse 60% 60% at 80% 80%, rgba(0,0,0,.25) 0%, transparent 70%),
            linear-gradient(175deg, rgba(255,255,255,.06) 0%, rgba(0,0,0,.30) 100%);
        }
        .lr2-cover-top { position: relative; padding: 28px 52px 0; display: flex; justify-content: space-between; align-items: flex-start; }
        .lr2-cover-brand { display: flex; align-items: center; gap: 10px; }
        .lr2-cover-brand-text { letter-spacing: .35em; font-family: sans-serif; font-size: 11px; color: rgba(255,255,255,.55); text-transform: uppercase; }
        .lr2-cover-brand-line { width: 1px; height: 18px; background: rgba(212,175,55,.4); }
        .lr2-cover-brand-sub { font-family: sans-serif; font-size: 9px; color: rgba(245,200,66,.55); letter-spacing: .12em; }
        .lr2-cover-type { text-align: right; border: 1px solid rgba(245,200,66,.35); padding: 5px 14px; border-radius: 2px; }
        .lr2-cover-type-zh { font-size: 11px; color: rgba(245,200,66,.85); letter-spacing: .1em; }
        .lr2-cover-type-en { font-family: sans-serif; font-size: 8px; color: rgba(245,200,66,.45); letter-spacing: .15em; margin-top: 2px; }
        .lr2-cover-center { position: relative; flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 32px 52px 20px; }
        .lr2-seal { width: 110px; height: 110px; border: 1.5px solid rgba(245,200,66,.5); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 24px; background: rgba(0,0,0,.12); position: relative; }
        .lr2-seal::after { content: ''; position: absolute; inset: 6px; border: 1px solid rgba(245,200,66,.25); border-radius: 50%; }
        .lr2-seal-zh { color: #F5C842; font-size: 15px; letter-spacing: .05em; display: block; text-align: center; }
        .lr2-seal-en { color: rgba(245,200,66,.5); font-family: sans-serif; font-size: 7.5px; letter-spacing: .15em; display: block; margin-top: 3px; text-align: center; }
        .lr2-cover-name { color: white; font-size: 32px; font-weight: 600; letter-spacing: .08em; margin-bottom: 6px; text-shadow: 0 2px 12px rgba(0,0,0,.4); }
        .lr2-cover-birth { color: rgba(245,200,66,.75); font-family: sans-serif; font-size: 12px; letter-spacing: .08em; margin-bottom: 20px; }
        .lr2-cover-pillars { display: flex; gap: 0; border: 1px solid rgba(245,200,66,.3); border-radius: 4px; overflow: hidden; margin-bottom: 16px; }
        .lr2-cover-pillar { padding: 10px 18px; text-align: center; border-right: 1px solid rgba(245,200,66,.2); }
        .lr2-cover-pillar:last-child { border-right: none; }
        .lr2-cover-pillar.day { background: rgba(245,200,66,.12); }
        .lr2-cover-pillar-gz { font-size: 20px; color: #F5C842; font-weight: 700; display: block; }
        .lr2-cover-pillar.day .lr2-cover-pillar-gz { font-size: 22px; color: #FFE070; }
        .lr2-cover-pillar-label { font-family: sans-serif; font-size: 9px; color: rgba(255,255,255,.35); letter-spacing: .1em; margin-top: 3px; display: block; }
        .lr2-cover-tags { display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; }
        .lr2-cover-tag { border: 1px solid rgba(245,200,66,.4); color: rgba(245,200,66,.8); font-family: sans-serif; font-size: 10px; padding: 4px 12px; border-radius: 20px; letter-spacing: .05em; }
        .lr2-cover-divider { position: relative; height: 1px; background: rgba(245,200,66,.2); margin: 20px 52px 0; }
        .lr2-cover-bottom { position: relative; padding: 16px 52px 28px; display: flex; justify-content: space-between; align-items: flex-end; }
        .lr2-cover-bottom-l { color: rgba(255,255,255,.4); font-family: sans-serif; font-size: 10px; line-height: 2.0; }
        .lr2-cover-bottom-r { text-align: right; color: rgba(245,200,66,.55); font-family: sans-serif; font-size: 10px; line-height: 2.0; }
        /* Page */
        .lr2-page { padding: 44px 56px; border-bottom: 1px solid #EDE4D0; position: relative; }
        .lr2-page::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: linear-gradient(180deg, #C9A84C 0%, #846B32 40%, transparent 100%); opacity: .5; }
        .lr2-ph { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 32px; padding-bottom: 16px; border-bottom: 1px solid #FBF3DC; }
        .lr2-ph-num { font-family: sans-serif; font-size: 10px; color: #9A8070; letter-spacing: .15em; padding-top: 4px; min-width: 24px; }
        .lr2-ph-title { font-size: 18px; font-weight: 700; color: #846B32; letter-spacing: .04em; font-family: 'Noto Serif SC', 'Source Han Serif SC', 'SimSun', Georgia, serif; }
        .lr2-ph-sub { font-family: sans-serif; font-size: 11px; color: #9A8070; letter-spacing: .1em; margin-top: 3px; }
        /* Step 1B: body/narrative → sans-serif; tables → sans-serif */
        .lr2-page p, .lr2-page li, .lr2-narrative { font-family: -apple-system, 'Noto Sans SC', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif; }
        .lr2-pgrid, .lr2-phead, .lr2-pshen, .lr2-ecount, .lr2-epct, .lr2-ebars { font-family: -apple-system, 'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif; font-weight: 400; }
        /* Table border → 印章暗金 (Step 3B) */
        .lr2-pgrid, .lr2-pcol, .lr2-phead { border-color: rgba(107,79,32,.15); }
        /* Step 4: page footer seal */
        .lr2-pf { display: flex; justify-content: space-between; align-items: center; margin-top: 32px; padding-top: 12px; border-top: 1px solid #EDE4D0; }
        .lr2-pf-seal { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; font-size: 11px; font-weight: 600; letter-spacing: 0; font-family: sans-serif; }
        .lr2-pf-current { background: rgba(212,175,55,.12); border: 1.5px solid rgba(212,175,55,.5); color: #8B6914; }
        .lr2-pf-label { color: rgba(154,128,112,.45); font-size: 9px; letter-spacing: .06em; font-family: sans-serif; }
        .lr2-section { margin-bottom: 32px; }
        .lr2-st { font-family: sans-serif; font-size: 11px; font-weight: 700; color: #A0813C; letter-spacing: .15em; text-transform: uppercase; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
        .lr2-st::after { content: ''; flex: 1; height: 1px; background: #FBF3DC; }
        .lr2-quote { border-top: 2px solid #C9A84C; border-bottom: 2px solid #C9A84C; background: #FFFBF0; padding: 18px 22px; margin: 24px 0; font-size: 13.5px; color: #4A3A2A; line-height: 1.9; font-style: italic; }
        .lr2-quote cite { display: block; text-align: right; margin-top: 8px; font-size: 10px; color: #9A8070; font-style: normal; font-family: sans-serif; letter-spacing: .05em; }
        .lr2-dawn { background: rgba(245,200,66,.12); border-left: 3px solid #F5C842; padding: 12px 16px; border-radius: 0 6px 6px 0; margin: 12px 0; font-size: 13px; line-height: 1.85; }
        .lr2-dawn strong { color: #846B32; }
        /* Pillars */
        .lr2-pgrid { display: grid; grid-template-columns: repeat(4, 1fr); border: 1px solid #E8D8A0; border-radius: 8px; overflow: hidden; margin-bottom: 24px; }
        .lr2-pcol { border-right: 1px solid #E8D8A0; }
        .lr2-pcol:last-child { border-right: none; }
        .lr2-phead { background: #FBF3DC; text-align: center; padding: 9px 4px; font-family: sans-serif; font-size: 10px; color: #846B32; font-weight: 700; letter-spacing: .1em; border-bottom: 1px solid #E8D8A0; }
        .lr2-pgz { text-align: center; padding: 18px 4px; font-size: 26px; font-weight: 700; border-bottom: 1px solid #EDE4D0; }
        .lr2-pgz.wood  { color: #2D6A4F; background: #F0FAF4; }
        .lr2-pgz.fire  { color: #B91C1C; background: #FFF0F0; }
        .lr2-pgz.earth { color: #A07820; background: #FEFAEB; }
        .lr2-pgz.metal { color: #6B7280; background: #F8F9FA; }
        .lr2-pgz.water { color: #1E3A8A; background: #EFF4FF; }
        .lr2-pshen { text-align: center; padding: 9px 6px; font-family: sans-serif; font-size: 10px; color: #9A8070; line-height: 1.6; }
        .lr2-pshen small { display: block; font-size: 9px; color: #BBA880; }
        @media (max-width:600px) { .lr2-pgrid { grid-template-columns: repeat(2,1fr);} .lr2-pcol { border-bottom: 1px solid #E8D8A0;} }
        /* Element bars */
        .lr2-ebars { display: flex; flex-direction: column; gap: 10px; }
        .lr2-erow { display: flex; align-items: center; gap: 12px; }
        .lr2-elabel { width: 28px; text-align: center; font-size: 14px; font-weight: 700; flex-shrink: 0; }
        .lr2-elabel.wood {color:#2D6A4F} .lr2-elabel.fire{color:#B91C1C} .lr2-elabel.earth{color:#A07820} .lr2-elabel.metal{color:#6B7280} .lr2-elabel.water{color:#1E3A8A}
        .lr2-ewrap { flex: 1; }
        .lr2-etrack { height: 9px; background: #EDE4D0; border-radius: 5px; overflow: hidden; }
        .lr2-efill { height: 100%; border-radius: 5px; transition: width .8s ease; }
        .lr2-efill.wood  { background: linear-gradient(90deg,#2D6A4F,#3D8A60); }
        .lr2-efill.fire  { background: linear-gradient(90deg,#B91C1C,#D43030); }
        .lr2-efill.earth { background: linear-gradient(90deg,#A07820,#C09030); }
        .lr2-efill.metal { background: linear-gradient(90deg,#6B7280,#8A9AB0); }
        .lr2-efill.water { background: linear-gradient(90deg,#1E3A8A,#2850C0); }
        .lr2-ecount { font-family: sans-serif; font-size: 10px; color: #9A8070; margin-top: 3px; }
        .lr2-epct { font-family: sans-serif; font-size: 11px; color: #9A8070; min-width: 36px; text-align: right; }
        .lr2-kw { display: flex; align-items: flex-start; gap: 10px; background: #FEE8E8; border: 1px solid #F5CCCC; border-radius: 6px; padding: 10px 16px; margin-top: 16px; font-size: 12.5px; color: #8B1A1A; }
        .lr2-kw strong { font-size: 14px; }
        /* Identity hero */
        .lr2-hero { background: linear-gradient(135deg, #FFFBF0 0%, #F5F0E8 100%); border: 1px solid #E8D8A0; border-radius: 8px; padding: 24px 28px; margin-bottom: 20px; }
        .lr2-hero-headline { font-size: 22px; font-weight: 700; color: #846B32; margin-bottom: 4px; }
        .lr2-hero-row { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
        .lr2-hero-badge { background: white; border: 1px solid #E8D8A0; padding: 4px 12px; border-radius: 20px; font-family: sans-serif; font-size: 11px; color: #6B5540; }
        .lr2-hero-badge strong { color: #846B32; }
        .lr2-hero-body { font-size: 14px; color: #333; line-height: 1.9; }
        .lr2-hero-body p { margin-bottom: 12px; }
        .lr2-hero-body p:last-child { margin-bottom: 0; }
        /* Xiyong cards */
        .lr2-xy { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
        @media (max-width:600px) { .lr2-xy { grid-template-columns: repeat(2,1fr);} }
        .lr2-xc { border-radius: 8px; padding: 16px 12px; text-align: center; border: 1px solid; }
        .lr2-xc-role { font-family: sans-serif; font-size: 9px; letter-spacing: .1em; margin-bottom: 8px; text-transform: uppercase; }
        .lr2-xc-elem { font-size: 22px; font-weight: 800; margin-bottom: 4px; }
        .lr2-xc-desc { font-family: sans-serif; font-size: 10px; color: #9A8070; margin-top: 6px; line-height: 1.5; }
        .lr2-xc-emoji { font-size: 20px; }
        .lr2-xc.yong { border-color: #93C5FD; background: #EFF6FF;} .lr2-xc.yong .lr2-xc-role{color:#1E40AF;} .lr2-xc.yong .lr2-xc-elem{color:#1E3A8A;}
        .lr2-xc.xi { border-color: #86EFAC; background: #F0FDF4;} .lr2-xc.xi .lr2-xc-role{color:#166534;} .lr2-xc.xi .lr2-xc-elem{color:#2D6A4F;}
        .lr2-xc.ji { border-color: #FDE68A; background: #FFFBEB;} .lr2-xc.ji .lr2-xc-role{color:#92400E;} .lr2-xc.ji .lr2-xc-elem{color:#A07820;}
        .lr2-xc.chou { border-color: #CBD5E1; background: #F8FAFC;} .lr2-xc.chou .lr2-xc-role{color:#4B5563;} .lr2-xc.chou .lr2-xc-elem{color:#6B7280;}
        /* Domain */
        .lr2-dgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 20px; }
        @media (max-width:600px) { .lr2-dgrid { grid-template-columns: 1fr;} }
        .lr2-dcard { background: white; border: 1px solid #EDE4D0; border-radius: 8px; padding: 18px 20px; }
        .lr2-dhead { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; }
        .lr2-dtitle { font-family: sans-serif; font-size: 12px; font-weight: 700; color: #A0813C; letter-spacing: .05em; }
        .lr2-dstars { font-size: 11px; color: #F5C842; letter-spacing: -1px; }
        .lr2-dbody { font-size: 13px; color: #333; line-height: 1.8; }
        .lr2-dbody p { margin-bottom: 8px; }
        .lr2-dkey { background: rgba(245,200,66,.12); border-radius: 4px; padding: 6px 10px; margin-top: 8px; font-size: 12px; color: #4A3A2A; }
        .lr2-dkey strong { color: #846B32; }
        /* Vajra bridge */
        .lr2-vajra { background: linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%); border: 1px solid #C4B5FD; border-radius: 8px; padding: 22px 26px; margin: 24px 0; }
        .lr2-vajra-head { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
        .lr2-vajra-icon { font-size: 22px; margin-top: 1px; }
        .lr2-vajra-title { font-size: 15px; font-weight: 700; color: #5B21B6; }
        .lr2-vajra-sub { font-family: sans-serif; font-size: 10px; color: #7C3AED; opacity: .7; margin-top: 2px; letter-spacing: .05em; }
        .lr2-vajra-body { font-size: 13px; color: #3B0764; line-height: 1.85; margin-bottom: 16px; }
        .lr2-vajra-body p { margin-bottom: 10px; }
        .lr2-vajra-tz { display: flex; align-items: center; gap: 6px; font-family: sans-serif; font-size: 10px; color: #6D28D9; margin-top: 8px; margin-bottom: 14px; background: rgba(109,40,217,.06); padding: 6px 10px; border-radius: 4px; }
        .lr2-vajra-cta { display: inline-flex; align-items: center; gap: 8px; background: #5B21B6; color: white; padding: 10px 22px; border-radius: 6px; font-family: sans-serif; font-size: 12px; letter-spacing: .05em; text-decoration: none; border: none; cursor: pointer; transition: background .2s; }
        .lr2-vajra-cta:hover { background: #4C1D95; }
        /* Timeline */
        .lr2-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 24px; }
        @media (max-width:600px) { .lr2-info-grid { grid-template-columns: 1fr;} }
        .lr2-itable { width: 100%; border-collapse: collapse; }
        .lr2-itable td { padding: 9px 12px; border-bottom: 1px solid #EDE4D0; font-size: 13px; }
        .lr2-itable td:first-child { background: #FFFBF0; color: #846B32; font-family: sans-serif; font-size: 10px; font-weight: 700; letter-spacing: .08em; width: 100px; }
        .lr2-tl { position: relative; padding-left: 24px; }
        .lr2-tl::before { content: ''; position: absolute; left: 7px; top: 8px; bottom: 8px; width: 2px; background: #FBF3DC; }
        .lr2-tli { position: relative; margin-bottom: 22px; }
        .lr2-tdot { position: absolute; left: -20px; top: 5px; width: 12px; height: 12px; border-radius: 50%; background: #A0813C; border: 2px solid #FDFBF7; }
        .lr2-tli.current .lr2-tdot { background: #846B32; box-shadow: 0 0 0 3px rgba(245,200,66,.12); }
        .lr2-tbadge { display: inline-block; background: #846B32; color: white; font-family: sans-serif; font-size: 9px; padding: 2px 8px; border-radius: 10px; margin-bottom: 4px; letter-spacing: .05em; }
        .lr2-tperiod { font-family: sans-serif; font-size: 10px; color: #9A8070; margin-bottom: 3px; }
        .lr2-tgz { font-size: 17px; font-weight: 700; color: #846B32; margin-bottom: 5px; }
        .lr2-ttheme { font-family: sans-serif; font-size: 10px; color: #A0813C; margin-bottom: 5px; font-weight: 600; }
        .lr2-tdesc { font-size: 13px; color: #4A3A2A; line-height: 1.7; }
        .lr2-lbars { display: flex; flex-direction: column; gap: 0; }
        .lr2-lrow { display: flex; align-items: center; gap: 12px; padding: 11px 0; border-bottom: 1px solid #EDE4D0; }
        .lr2-lrow:last-child { border-bottom: none; }
        .lr2-lyear { font-family: sans-serif; font-size: 11px; color: #9A8070; min-width: 38px; }
        .lr2-lgz { font-size: 15px; font-weight: 700; color: #846B32; min-width: 30px; }
        .lr2-ltheme { font-family: sans-serif; font-size: 10px; color: #9A8070; flex: 1; }
        .lr2-ltrack { width: 100px; height: 6px; background: #EDE4D0; border-radius: 3px; overflow: hidden; }
        .lr2-lfill { height: 100%; border-radius: 3px; transition: width .8s ease; }
        .lr2-lfill.high   { background: linear-gradient(90deg,#2D6A4F,#5CB87E); }
        .lr2-lfill.medium { background: linear-gradient(90deg,#A07820,#D4A030); }
        .lr2-lfill.low    { background: linear-gradient(90deg,#6B7280,#9AB0C0); }
        .lr2-lnum { font-family: sans-serif; font-size: 11px; color: #9A8070; min-width: 28px; text-align: right; }
        .lr2-actions { list-style: none; padding-left: 0; margin: 0; }
        .lr2-actions li { display: flex; gap: 16px; padding: 14px 0; border-bottom: 1px solid #EDE4D0; align-items: flex-start; }
        .lr2-actions li:last-child { border-bottom: none; }
        .lr2-anum { font-family: sans-serif; font-size: 18px; font-weight: 800; color: #C9A84C; min-width: 20px; line-height: 1.3; }
        .lr2-atext { font-size: 13.5px; color: #333; line-height: 1.8; }
        .lr2-atext strong { color: #846B32; }
        /* Q&A */
        .lr2-qa { background: white; border: 1px solid #EDE4D0; border-left: 3px solid #A0813C; border-radius: 8px; padding: 18px 22px; margin-bottom: 14px; }
        .lr2-qa-q { display: flex; gap: 10px; align-items: flex-start; margin-bottom: 12px; }
        .lr2-qa-badge { background: #846B32; color: white; font-family: sans-serif; font-size: 10px; padding: 2px 8px; border-radius: 4px; flex-shrink: 0; margin-top: 2px; }
        .lr2-qa-text { font-size: 14px; font-weight: 700; color: #333; line-height: 1.6; }
        .lr2-qa-a { font-size: 13.5px; color: #333; line-height: 1.9; }
        .lr2-qa-a p { margin-bottom: 10px; }
        .lr2-verdict { display: inline-flex; align-items: center; gap: 6px; margin-top: 12px; font-family: sans-serif; font-size: 11px; padding: 5px 12px; border-radius: 5px; font-weight: 600; }
        .lr2-verdict.pos { background: #D1FAE5; color: #065F46; }
        .lr2-verdict.cau { background: rgba(245,200,66,.12); color: #78450A; }
        .lr2-verdict.neg { background: #FEE2E2; color: #991B1B; }
        /* Harmony */
        .lr2-hgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 20px; }
        @media (max-width:600px) { .lr2-hgrid { grid-template-columns: 1fr;} }
        .lr2-hcard { background: white; border: 1px solid #EDE4D0; border-radius: 8px; padding: 18px 20px; }
        .lr2-htitle { font-family: sans-serif; font-size: 11px; font-weight: 700; color: #A0813C; letter-spacing: .1em; margin-bottom: 12px; text-transform: uppercase; }
        .lr2-hlist { list-style: none; padding-left: 0; margin: 0; }
        .lr2-hlist li { display: flex; gap: 10px; padding: 8px 0; border-bottom: 1px solid #EDE4D0; font-size: 13px; color: #333; align-items: flex-start; }
        .lr2-hlist li:last-child { border-bottom: none; }
        .lr2-hkey { min-width: 52px; font-family: sans-serif; font-size: 10px; color: #846B32; font-weight: 700; padding-top: 2px; }
        /* Credentials */
        .lr2-cred { background: linear-gradient(135deg,#F5F3FF,#EDE9FE); border: 1px solid #C4B5FD; border-radius: 8px; padding: 22px 26px; margin-top: 24px; }
        .lr2-cred-title { font-size: 14px; font-weight: 700; color: #5B21B6; margin-bottom: 4px; }
        .lr2-cred-sub { font-family: sans-serif; font-size: 10px; color: #7C3AED; opacity: .7; margin-bottom: 16px; letter-spacing: .05em; }
        .lr2-cred-item { margin-bottom: 14px; }
        .lr2-cred-head { display: flex; justify-content: space-between; margin-bottom: 6px; }
        .lr2-cred-name { font-size: 13px; color: #3B0764; font-weight: 600; }
        .lr2-cred-pct { font-family: sans-serif; font-size: 11px; color: #6D28D9; }
        .lr2-cred-track { height: 7px; background: rgba(109,40,217,.12); border-radius: 4px; overflow: hidden; }
        .lr2-cred-fill { height: 100%; border-radius: 4px; background: linear-gradient(90deg,#7C3AED,#A855F7); transition: width .8s ease; }
        /* Back cover */
        .lr2-back { background: #EDE4D0; padding: 52px 56px; text-align: center; }
        .lr2-back-title { font-size: 20px; font-weight: 700; color: #846B32; margin-bottom: 4px; }
        .lr2-back-sub { font-family: sans-serif; font-size: 11px; color: #9A8070; margin-bottom: 28px; letter-spacing: .08em; }
        .lr2-ethics { text-align: left; max-width: 420px; margin: 0 auto 28px; list-style: none; padding-left: 0; }
        .lr2-ethics li { display: flex; gap: 12px; padding: 9px 0; border-bottom: 1px solid #F5F0E8; align-items: flex-start; }
        .lr2-ethics li:last-child { border-bottom: none; }
        .lr2-ethics-dot { width: 6px; height: 6px; border-radius: 50%; background: #C9A84C; flex-shrink: 0; margin-top: 6px; }
        .lr2-ethics-text { font-size: 12.5px; color: #6B5540; line-height: 1.6; }
        .lr2-back-foot { font-family: sans-serif; font-size: 10px; color: #9A8070; line-height: 2.2; }
        .lr2-back-foot small { font-size: 9px; opacity: .6; display: block; margin-top: 4px; }
        /* Action bar */
        .lr2-actbar { max-width: 860px; margin: 24px auto 0; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; padding: 16px 20px; background: rgba(255,255,255,.72); backdrop-filter: blur(12px); border: 1px solid rgba(212,175,55,.22); border-radius: 12px; }
        .lr2-actbar-disclaimer { font-size: 12px; color: #6B5540; max-width: 360px; }
        .lr2-actbar-buttons { display: flex; gap: 10px; flex-wrap: wrap; }
        .lr2-btn { border-radius: 8px; padding: 10px 22px; font-size: 14px; font-weight: 600; cursor: pointer; letter-spacing: .04em; transition: opacity .2s; border: none; }
        .lr2-btn:disabled { cursor: not-allowed; opacity: .65; }
        .lr2-btn.primary { background: linear-gradient(135deg,#846B32,#C9A84C); color: white; border: 1px solid rgba(212,175,55,.55); box-shadow: 0 2px 12px rgba(132,107,50,.35); }
        .lr2-btn.secondary { background: linear-gradient(135deg,#064E3B,#0a7a58); color: white; border: 1px solid rgba(212,175,55,.4); }
        .lr2-btn.ghost { background: transparent; color: #846B32; border: 1px solid #C9A84C; }
        @media (max-width:640px) { .lr2-page { padding: 28px 22px; } .lr2-cover-top, .lr2-cover-center, .lr2-cover-bottom, .lr2-cover-divider { padding-left: 24px; padding-right: 24px; } }
        @media print { .no-print { display: none !important; } .lr2-shell { background: white !important; } .lr2-report { box-shadow: none !important; margin: 0 !important; } .lr2-page, .lr2-back { page-break-before: always; } .lr2-cover, .lr2-page, .lr2-back { break-inside: avoid; } .lr2-dcard, .lr2-dgrid, .lr2-ebars, .lr2-section, .monthly-row, .lr2-pgrid { page-break-inside: avoid !important; break-inside: avoid !important; } .lr2-ca-dim { page-break-inside: avoid !important; } }
      `}</style>

      {/* ── Sticky proto-nav ── */}
      <div className="lr2-nav no-print">
        <span className="lr2-nav-brand">{lang==="zh"?"澄光能量审计报告":"LUMINAARC Energy Audit Report"}</span>
        <a onClick={()=>document.getElementById('cover')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"封面":"Cover"}</a>
        <a onClick={()=>document.getElementById('ch1')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"命盘":"Pillars"}</a>
        <a onClick={()=>document.getElementById('ch2')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"我是谁":"Identity"}</a>
        {_backend?.shishen_full && <a onClick={()=>document.getElementById('ch2b')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"十神":"Shishen"}</a>}
        <a onClick={()=>document.getElementById('ch3')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"领域":"Domains"}</a>
        {_backend?.monthly_fortunes?.length > 0 && <a onClick={()=>document.getElementById('ch3b')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"运程":"Monthly"}</a>}
        <a onClick={()=>document.getElementById('ch4')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"运势":"Cycles"}</a>
        {_backend?.current_period?.layer_analysis && <a onClick={()=>document.getElementById('ch4b')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"三层":"Layers"}</a>}
        {hasQuestions && <a onClick={()=>document.getElementById('ch5')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"问题":"Q&A"}</a>}
        <a onClick={()=>document.getElementById('ch6')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"调和":"Harmony"}</a>
        {_backend?.xiyongshen && <a onClick={()=>document.getElementById('ch6b')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"对治":"Protocol"}</a>}
        <a onClick={()=>document.getElementById('ch6c')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"五大":"5Elem"}</a>
        {_backend?.ziwei_full?.palaces?.length > 0 && <a onClick={()=>document.getElementById('ch7b')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"紫微":"Ziwei"}</a>}
        <a onClick={()=>document.getElementById('back')?.scrollIntoView({behavior:'smooth'})}>{lang==="zh"?"封底":"Back"}</a>
        <span className="lr2-nav-badge">{result._source==="backend"?"v2 · PRO":"v2 · LOCAL"}</span>
      </div>

      <div className="lr2-report">

        {/* ── Cover ── */}
        <CoverPage
          pillars={pillars}
          name={name}
          birthLine={birthLine}
          reportNumber={reportNumber}
          genDate={genDate}
          dayStem={dayStem}
          dayElemZh={dayElemZh}
          strengthLabel={strengthLabel}
          kwHasData={kwHasData}
          kwBranches={kwBranches}
          yongElem={yongElem}
          result={result}
        />

        {/* ── 关键结论摘要 (移动端友好) ── */}
        {(() => {
          const dm = _backend?.daymaster || {};
          const xy = _backend?.xiyongshen || {};
          const sc = _backend?.strength || {};
          const yong = xy.yong_shen || "?";
          const xi = xy.xi_shen || "?";
          const cat = sc.category || "?";
          const score = Math.round(sc.strength_score || 0);
          const cp = _backend?.current_period || {};
          const currDy = cp.dayun || "?";
          const nd = cp.next_dayun || {};
          const ndGz = nd.ganzhi || "?";
          const ndAge = nd.start_age || "?";
          const ziwei = _backend?.ziwei_full || {};
          const palaces = ziwei.palaces || [];
          let tzStars = "—";
          let sgInfo = null;
          for (const p of palaces) {
            const stars = (p.stars || []).slice(0, 3).join("、") || "—";
            if (p.palace_name === "田宅") tzStars = stars;
            if (p.is_shen_gong) sgInfo = { name: p.palace_name, stars };
          }
          const items = [
            `${lang==="zh"?"日主":"Daymaster"}: ${dm.stem||"?"}${dm.element||"?"} · ${cat}(${score}/100)`,
            `${lang==="zh"?"用神":"Key element"}: ${yong} · ${lang==="zh"?"喜神":"Support"}: ${xi}`,
            lang==="zh"
              ? `⏳ 大运${currDy} → 下一程${ndAge}岁${ndGz}`
              : `⏳ Cycle: ${currDy} → Next: ${ndGz} at ${ndAge}`,
            `${lang==="zh"?"🏠 田宅":"🏠 Property"}: ${tzStars}${lang==="zh"?" · 家宅房产":" · home"}`,
            sgInfo ? `${lang==="zh"?`💼 身宫(${sgInfo.name})`:`💼 ShenGong(${sgInfo.name})`}: ${sgInfo.stars}` : null,
          ].filter(Boolean);
          return (
            <div style={{maxWidth:860,margin:"16px auto 0",background:"rgba(6,78,59,0.06)",border:"1px solid rgba(212,175,55,.2)",borderRadius:8,padding:"14px 20px"}}>
              <div style={{fontSize:12,color:"#A0813C",fontWeight:600,marginBottom:8,letterSpacing:".05em"}}>{lang==="zh"?"📌 关键结论":"📌 Key Takeaways"}</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {items.map((t,i)=><span key={i} style={{fontSize:12,color:"#444",background:"white",padding:"4px 10px",borderRadius:14,border:"1px solid rgba(212,175,55,.15)",whiteSpace:"nowrap"}}>{t}</span>)}
              </div>
            </div>
          );
        })()}

        {/* ── Phase 83F: 穷通宝鉴调候用神 ── */}
        {(() => {
          const th = _backend?.tiaohou || {};
          if (!th.tiaohou_primary) return null;
          const conflict = th.conflict;
          const borderColor = conflict ? "#DC2626" : "#D4AF37";
          const bgColor = conflict ? "rgba(220,38,38,0.04)" : "rgba(212,175,55,0.06)";
          return (
            <div style={{maxWidth:860,margin:"12px auto 0",background:bgColor,border:`1px solid ${borderColor}`,borderRadius:8,padding:"12px 20px"}}>
              <div style={{fontSize:12,color:conflict?"#DC2626":"#A0813C",fontWeight:600,marginBottom:6}}>
                {lang==="zh"?"🕯️ 古法调候 · 穷通宝鉴":"🕯️ Classical Tiaohou · QiongTongBaoJian"}
                {conflict && (lang==="zh"?" ⚠️ 与格局用神有差异":" ⚠️ Differs from structural analysis")}
              </div>
              <div style={{fontSize:11.5,color:"#555",lineHeight:1.8}}>
                <b>{lang==="zh"?"调候用神":"Tiaohou Key"}:</b> {th.tiaohou_elems?.join("、") || "—"}
                {" · "}{lang==="zh"?`优先: ${th.priority}`:`Priority: ${th.priority}`}
              </div>
              <div style={{fontSize:11,color:"#777",marginTop:4,fontStyle:"italic",lineHeight:1.7}}>
                「{th.citation}」
              </div>
              {th.reason && (
                <div style={{fontSize:11,color:"#666",marginTop:4,lineHeight:1.7}}>
                  {th.reason}
                </div>
              )}
              {conflict && th.conflict_note && (
                <div style={{fontSize:11,color:"#DC2626",marginTop:6,padding:"6px 10px",background:"rgba(220,38,38,0.06)",borderRadius:4,lineHeight:1.7}}>
                  {th.conflict_note}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── 阅读指南 ── */}
        <div className="lr2-page">
          <div style={{fontSize:15,color:"#A0813C",fontWeight:700,marginBottom:12}}>{lang==="zh"?"📖 如何阅读这份报告":"📖 How to Read This Report"}</div>
          <div style={{fontSize:13,color:"#555",lineHeight:2.0}}>
            {lang==="zh"
              ? <>这份报告共四个层次：① <b>命盘</b>（第1-2章）是你的能量底色——先读懂自己的出厂配置。② <b>领域分析</b>（第3章+逐月运程）看今年四个关键领域的具体走势。③ <b>运势时间轴</b>（第4章）把大运、流年、流月三层叠加看时机。④ <b>能量调和</b>（第6章）是工具箱——方位、颜色、节律、修持。<br/>紫微章节（第7章）是八字之外的交叉验证——如果看不懂术语，只看开头那段白话总结就够了。</>
              : <>This report has 4 layers: ① <b>Chart</b> (Ch1-2) — your energy blueprint. ② <b>Domains</b> (Ch3 + Monthly) — career/love/family/health this year. ③ <b>Timeline</b> (Ch4) — luck cycles layered together. ④ <b>Harmonization</b> (Ch6) — your toolkit of directions, colors, rhythms.<br/>Ziwei section (Ch7) is a cross-check — if the terms feel dense, just read the plain-language summary at the top.</>}
          </div>
        </div>

        {/* ── Ch1: Four Pillars ── */}
        <PillarsPage
          pillars={pillars}
          reportNumber={reportNumber}
          shishen={shishen}
          _pctMap={_pctMap}
          isOver={isOver}
          isUnder={isUnder}
          ELEM_LABEL={ELEM_LABEL}
          kwHasData={kwHasData}
          kwRotation={kwRotation}
          kwBranches={kwBranches}
          kw={kw}
          _backend={_backend}
          yongElem={yongElem}
          lang={lang}
        />

        {/* ── Ch2: Identity ── */}
        <IdentityPage
          pillars={pillars}
          dayStem={dayStem}
          dayElemZh={dayElemZh}
          strengthScore={strengthScore}
          strengthLabel={strengthLabel}
          yongElem={yongElem}
          xiElem={xiElem}
          jiElem={jiElem}
          chouElem={chouElem}
          yongDesc={yongDesc}
          xiDesc={xiDesc}
          jiDesc={jiDesc}
          chouDesc={chouDesc}
          ELEM_EMOJI={ELEM_EMOJI}
          _backend={_backend}
          _narratives={_narratives}
          reportNumber={reportNumber}
          lang={lang}
        />

        {/* ── Ch2B: 十神全像 ── */}
        {_backend?.shishen_full && (
          <div className="lr2-page" id="ch2b">
            <div className="lr2-ph">
              <span className="lr2-ph-num">02B</span>
              <div>
                <div className="lr2-ph-title">十神全像 · 逐项解析</div>
                <div className="lr2-ph-sub">FULL SHISHEN PROFILE · TEN GODS ANALYSIS</div>
              </div>
            </div>
            <ShishenCards
              shishen_full={_backend.shishen_full}
              daymaster={_backend?.daymaster || {stem: dayStem, element: dayElemZh}}
              lang={lang}
            />
            {_narratives?.shishen_profile && (
              <div style={{marginTop:18}}>
                <div className="lr2-st">十神格局 · 人生性格画像</div>
                <div style={{fontSize:"13.5px",color:"#333",lineHeight:1.9}}>
                  {_narratives.shishen_profile.split("\n").filter(Boolean).map((para,i)=>(
                    <p key={i} style={{marginBottom:10}}>{para}</p>
                  ))}
                </div>
              </div>
            )}
            <div className="lr2-pf"><span className="lr2-pf-label">澄光能量 · {reportNumber}</span><span className="lr2-pf-seal lr2-pf-current">2B</span></div>
          </div>
        )}

        {/* ── Ch02C: 纳音神煞 (古法·Phase 61) ── */}
        {_backend?.nayin_shensha && (
          <div className="lr2-page" id="ch02c">
            <div className="lr2-ph">
              <div className="lr2-ph-icon">☯</div>
              <span className="lr2-ph-num">02C</span>
              <div>
                <div className="lr2-ph-title">纳音神煞 · 古法年命</div>
                <div className="lr2-ph-sub">NAYIN · SHENSHA · ANCIENT METHOD · {_backend.nayin_shensha.year_nayin}之命</div>
              </div>
            </div>
            <NayinShenshaSection
              data={_backend.nayin_shensha}
              narrative={_narratives?.nayin_shensha_narrative}
            />
            <div className="lr2-pf"><span className="lr2-pf-label">澄光能量 · {reportNumber}</span><span className="lr2-pf-seal lr2-pf-current">2C</span></div>
          </div>
        )}

        {/* ── Ch3: Four Domains ── */}
        <DomainCards
          v2Domains={v2Domains}
          domain_depth={_backend?.domain_depth}
          reportNumber={reportNumber}
          yongElem={yongElem}
          xiElem={xiElem}
          jiElem={jiElem}
          chouElem={chouElem}
          tiaohou={_backend?.tiaohou}
          setPage={setPage}
          lang={lang}
          _narratives={_narratives}
          age={2026 - (birthY || 1980)}
        />

        {/* ── Ch3B: 36月运程 ── */}
        {_backend?.monthly_fortunes?.length > 0 && (
          <div className="lr2-page" id="ch3b">
            <div className="lr2-ph">
              <span className="lr2-ph-num">03B</span>
              <div>
                <div className="lr2-ph-title">2026年逐月能量走势</div>
                <div className="lr2-ph-sub">36-MONTH ENERGY FORECAST</div>
              </div>
            </div>
            <MonthlyTimeline
              monthly_fortunes={_backend.monthly_fortunes}
              lang={lang}
            />
            <div className="lr2-pf"><span className="lr2-pf-label">澄光能量 · {reportNumber}</span><span className="lr2-pf-seal lr2-pf-current">3B</span></div>
          </div>
        )}

        {/* ── Ch4: Timeline ── */}
        <CyclesPage
          dayunSlice={dayunSlice}
          birthY={birthY}
          _backend={_backend}
          liunian3={liunian3}
          actionGuidance={actionGuidance}
          _narratives={_narratives}
          reportNumber={reportNumber}
          lang={lang}
        />

        {/* ── Ch4B: 三层作用分析 ── */}
        {_backend?.current_period?.layer_analysis && (
          <div className="lr2-page" id="ch4b">
            <div className="lr2-ph">
              <span className="lr2-ph-num">04B</span>
              <div>
                <div className="lr2-ph-title">三层作用 · 深度分析</div>
                <div className="lr2-ph-sub">LAYER ANALYSIS · CYCLE INTERACTIONS</div>
              </div>
            </div>
            <LayerAnalysis
              layer_analysis={_backend.current_period.layer_analysis}
              stem_branch_division={_backend.current_period.stem_branch_division}
              lang={lang}
            />
            {_narratives?.layer_analysis_narrative && (
              <div style={{marginTop:18}}>
                <div className="lr2-st">三层作用 · 深度解读</div>
                <div style={{fontSize:"13.5px",color:"#333",lineHeight:1.9}}>
                  {_narratives.layer_analysis_narrative.split("\n").filter(Boolean).map((para,i)=>(
                    <p key={i} style={{marginBottom:10}}>{para}</p>
                  ))}
                </div>
              </div>
            )}
            <div className="lr2-pf"><span className="lr2-pf-label">澄光能量 · {reportNumber}</span><span className="lr2-pf-seal lr2-pf-current">4B</span></div>
          </div>
        )}

        {/* ── Ch5: Q&A (conditional) ── */}
        {hasQuestions && (
          <div className="lr2-page" id="ch5">
            <div className="lr2-ph">
              <span className="lr2-ph-num">05</span>
              <div>
                <div className="lr2-ph-title">特定问题解析</div>
                <div className="lr2-ph-sub">SPECIFIC SITUATION ANALYSIS</div>
              </div>
            </div>
            <div style={{background:"#FFFBF0",border:"1px solid #E8D8A0",borderRadius:8,padding:"14px 18px",marginBottom:24,fontSize:13,color:"#6B5540"}}>
              以下基于您在生成报告时提交的具体问题，结合四柱命盘、当前大运流年进行逐项分析。
            </div>
            {(questions.length > 0 ? questions : qaAnswers.map(a => a.question)).map((q,i)=>{
              const a = qaAnswers[i];
              const verdict = a?.verdict || "cau";
              const verdictText = a?.verdict_text || (lang==="zh"?"◐ 综合判断 · 详见澄光完整版 PDF":"◐ Summary verdict · see Pro PDF");
              return (
                <div key={i} className="lr2-qa">
                  <div className="lr2-qa-q">
                    <span className="lr2-qa-badge">问 {i+1}</span>
                    <span className="lr2-qa-text">{q}</span>
                  </div>
                  <div className="lr2-qa-a">
                    {a?.body
                      ? a.body.split(/\n+/).filter(Boolean).map((p,j)=><p key={j}>{p}</p>)
                      : <p style={{color:"#9A8070",fontStyle:"italic"}}>{lang==="zh"?"这个问题的答案藏在你命盘的 3 个交叉点里——大运 × 流年 × 喜用神。澄光完整版会把这 3 条线索拆开来逐一回答你，并给出具体的时机窗口（精确到月份）。":"This answer hides at three intersections in your chart — luck cycle × annual × favorable element. The LUMINAARC Pro version unpacks these and gives a month-precision timing window."}</p>
                    }
                  </div>
                  <span className={"lr2-verdict "+verdict}>{verdictText}</span>
                </div>
              );
            })}
            <div style={{marginTop:20,padding:"16px 20px",background:"#F5F0E8",borderRadius:8,fontSize:13,color:"#6B5540",lineHeight:1.8}}>
              <strong style={{color:"#846B32"}}>💡 追问功能：</strong>
              如需就以上问题深入追问，可通过本页底部的「💬 向澄光提问」对话框实时提问。L1 订阅会员享有每月 10 次深度对话额度。
            </div>
            <div className="lr2-pf"><span className="lr2-pf-label">澄光能量 · {reportNumber}</span><span className="lr2-pf-seal lr2-pf-current">5</span></div>
          </div>
        )}

        {/* ── Ch6: Harmony ── */}
        <HarmonyPage
          harmDirections={harmDirections}
          harmColors={harmColors}
          harmIndustries={harmIndustries}
          harmRhythm={harmRhythm}
          _narratives={_narratives}
          yongElem={yongElem}
          userCredentials={userCredentials}
          reportNumber={reportNumber}
          setPage={setPage}
          lang={lang}
          juvenile={_isJuvenile}
        />

        {/* ── Ch6B: 年度对治协议 ── */}
        {_backend?.xiyongshen && (
          <div className="lr2-page" id="ch6b">
            <div className="lr2-ph">
              <span className="lr2-ph-num">06B</span>
              <div>
                <div className="lr2-ph-title">年度对治协议 · 四季调适</div>
                <div className="lr2-ph-sub">YEARLY ENERGY PROTOCOL · SEASONAL PRACTICE</div>
              </div>
            </div>
            <YearlyProtocol
              xiyongshen={_backend.xiyongshen}
              strength={_backend.strength}
              current_period={_backend.current_period}
              lang={lang}
            />
            <div className="lr2-pf"><span className="lr2-pf-label">澄光能量 · {reportNumber}</span><span className="lr2-pf-seal lr2-pf-current">6B</span></div>
          </div>
        )}

        {/* ── Phase 82: 五行→五大 能量翻译 ── */}
        {_backend?.daymaster && (
          <FiveElementsSection
            dayElem={_backend.daymaster.element || dayElemZh}
            yongElem={yongElem}
            user={user}
            lang={lang}
          />
        )}

        {/* ── Ch7B: 紫微12宫 ── */}
        {_backend?.ziwei_full?.palaces?.length > 0 && (
          <div className="lr2-page" id="ch7b">
            <div className="lr2-ph">
              <span className="lr2-ph-num">07B</span>
              <div>
                <div className="lr2-ph-title">紫微十二宫 · 全景详解</div>
                <div className="lr2-ph-sub">ZIWEI TWELVE PALACES · FULL CHART</div>
              </div>
            </div>
            <ZiweiPalaces
              ziwei_full={_backend.ziwei_full}
              ziwei_interpretation={_backend.ziwei_interpretation}
              lang={lang}
            />
            <div className="lr2-pf"><span className="lr2-pf-label">澄光能量 · {reportNumber}</span><span className="lr2-pf-seal lr2-pf-current">7B</span></div>
          </div>
        )}

        {/* ── Phase 48A: 能量交叉透视 (L1+ only) ── */}
        {_backend?.cross_analysis && !_backend.cross_analysis.error && (
        <div className="lr2-page">
          {_narratives?.cross_analysis_narrative && (
            <div style={{marginBottom:18}}>
              <div className="lr2-st">双系统融合洞见</div>
              <div style={{fontSize:"13.5px",color:"#333",lineHeight:1.9}}>
                {_narratives.cross_analysis_narrative.split("\n").filter(Boolean).map((para,i)=>(
                  <p key={i} style={{marginBottom:10}}>{para}</p>
                ))}
              </div>
            </div>
          )}
          <EnergyCrossAnalysis data={_backend.cross_analysis} lang={lang} />
        </div>
      )}

      </div>

      {/* ── Phase 37: 「然后呢」 module — only for free/unauthenticated ── */}
      {(!user || user.tier === "free") && (
      <div className="no-print" style={{maxWidth:860,margin:"32px auto 0"}}>
        <div style={{
          background:"#FDFBF7",
          border:"1px solid #E8D8A0",
          borderLeft:"4px solid #C9A84C",
          borderRadius:8,
          padding:"28px 32px",
          boxShadow:"0 4px 24px rgba(132,107,50,.08)",
        }}>
          <div style={{
            fontFamily:"'Inter','Montserrat',sans-serif",
            fontSize:11,letterSpacing:".18em",color:"#A0813C",
            textTransform:"uppercase",marginBottom:8,
          }}>
            {lang==="zh"?"然后呢":"What now?"}
          </div>
          <div style={{
            fontFamily:"'Noto Serif SC','PingFang SC',serif",
            fontSize:20,fontWeight:600,color:"#846B32",
            lineHeight:1.5,marginBottom:20,
          }}>
            {lang==="zh"
              ? "你的命盘留下了 3 个未解答的问题"
              : "Your chart leaves 3 questions unanswered"}
          </div>
          <ul style={{listStyle:"none",padding:0,margin:0,marginBottom:24}}>
            {(() => {
              // 动态生成 3 个具体问题（基于真实命盘数据）
              const yong = yongElem || (lang==="zh"?"用神":"the favorable element");
              const dy = _backend?.current_period?.dayun || dayunSlice[0]?.ganzhi || (lang==="zh"?"当前大运":"current luck cycle");
              const ln = _backend?.current_period?.liunian || "丙午";
              // 找最低分领域作为 Q2 锚点
              const weakestDomain = v2Domains
                .slice()
                .sort((a,b)=>(a.score||50)-(b.score||50))[0];
              const dName = weakestDomain?.name || (lang==="zh"?"事业":"career");
              const kwHint = kwHasData
                ? (lang==="zh"?`空亡的 ${kwBranches.join("")} 在今年会以什么形式出现？`:`How will the kongwang (${kwBranches.join(", ")}) manifest this year?`)
                : (lang==="zh"?`今年 ${ln} 流年最值得把握的窗口在几月？`:`In which month does ${ln} open the most actionable window?`);
              const qs = lang==="zh"
                ? [
                    `你的「${yong}」为什么总是不容易得到？背后的 2 个具体模式是什么？`,
                    `${dy} 大运对你的「${dName}」意味着什么？这 10 年里哪 3 年是关键？`,
                    kwHint,
                  ]
                : [
                    `Why is your "${yong}" so hard to receive? What are the 2 specific patterns behind it?`,
                    `What does the ${dy} cycle mean for your "${dName}"? Which 3 years in this decade are pivotal?`,
                    kwHint,
                  ];
              return qs.map((q,i)=>(
                <li key={i} style={{
                  display:"flex",gap:12,padding:"10px 0",
                  borderBottom: i<2?"1px solid #F5F0E8":"none",
                  fontSize:14,color:"#333",lineHeight:1.7,
                }}>
                  <span style={{
                    fontFamily:"sans-serif",fontSize:13,fontWeight:700,
                    color:"#C9A84C",minWidth:18,
                  }}>{i+1}.</span>
                  <span>{q}</span>
                </li>
              ));
            })()}
          </ul>
          <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:16}}>
            {/* CTA1 — 主：跳转 pricing */}
            <button onClick={()=>setPage&&setPage("pricing")}
              style={{
                background:"linear-gradient(135deg,#846B32,#C9A84C)",
                color:"white",border:"none",
                borderRadius:8,padding:"12px 24px",
                fontSize:14,fontWeight:600,cursor:"pointer",
                letterSpacing:".05em",
                boxShadow:"0 2px 12px rgba(132,107,50,.35)",
              }}>
              {lang==="zh"?"📋 获取完整解答 · $149":"📋 Get Full Answers · $149"}
            </button>
            {/* CTA2 — 副：打开 ChatDrawer */}
            {user && API && (
              <button onClick={()=>setChatOpen(true)}
                style={{
                  background:"transparent",
                  color:"#846B32",
                  border:"1px solid #C9A84C",
                  borderRadius:8,padding:"12px 24px",
                  fontSize:14,fontWeight:500,cursor:"pointer",
                  letterSpacing:".05em",
                }}>
                {lang==="zh"?"💬 先免费问一个问题":"💬 Ask one free question first"}
              </button>
            )}
          </div>
          <div style={{
            fontSize:12,color:"#9A8070",
            paddingTop:14,borderTop:"1px dashed #E8D8A0",
            lineHeight:1.7,
          }}>
            {lang==="zh"
              ? "完整版报告包含：特定问题逐题解析（精确到月份的时机建议） · 3 年运势详图 · 能量调和方案（方位/颜色/行业/节律） · 紫微大限双系统印证。"
              : "The Pro version includes: per-question analysis with month-precision timing · 3-year detailed outlook · harmony plan (direction/color/industry/rhythm) · ZiWei dual-system confirmation."}
          </div>
        </div>
      </div>
      )}

      {/* ── BlurGate for free tier only — pro users get full access ── */}
      {(!user || user.tier === "free") && (
        <div style={{maxWidth:860,margin:"24px auto 0"}}>
          <PersonalizedInsightPanel
            data={null}
            lang={lang}
            user={user}
            setPage={setPage}
          />
        </div>
      )}

      {/* ── Phase 60: 用户反馈校准 ── */}
      {reportNumber && (
        <FeedbackSection
          reportId={reportNumber}
          reportType="pro"
          api={API}
        />
      )}

      {/* ── Phase 78: 能量层·烦恼匹配入口 ── */}
      <EnergyMatchCard
        data={result?._backend?._energy_match || result?._backend?.energy_match || null}
        lang={lang}
      />

      {/* ── Back cover (last content element) ── */}
      <BackCover reportNumber={reportNumber} genDate={genDate} />

      {/* Phase C: Oracle 追问 CTA — 报告读完后的下一步 */}
      <div className="no-print" style={{
        maxWidth: 720, margin: "32px auto 0", padding: "24px 28px",
        background: "rgba(212,175,55,0.04)", border: "1px solid rgba(212,175,55,0.15)",
        borderRadius: 14, textAlign: "center",
      }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "rgba(0,0,0,0.75)", letterSpacing: ".04em", marginBottom: 6 }}>
          {lang === "zh" ? "💡 看完报告，还有具体问题？" : "💡 Have specific questions after reading?"}
        </div>
        <div style={{ fontSize: 13, color: "rgba(0,0,0,0.45)", marginBottom: 14, lineHeight: 1.6 }}>
          {lang === "zh"
            ? "澄光 Oracle 可以针对你的事业、感情、决策逐条追问——基于同一份命盘，不另收费。"
            : "Ask follow-up questions about career, relationships, or decisions — same chart, no extra cost."}
        </div>
        <a href="/oracle" onClick={(e) => { e.preventDefault(); window.location.href = "/oracle"; }} style={{
          display: "inline-block", padding: "10px 28px", borderRadius: 10,
          background: "linear-gradient(135deg,#D4AF37,#064E3B)", color: "#fff",
          fontSize: 14, fontWeight: 600, textDecoration: "none",
          letterSpacing: ".04em",
        }}>
          {lang === "zh" ? "🧭 去 Oracle 追问 →" : "🧭 Ask Oracle →"}
        </a>
      </div>

      {/* ── Bottom action bar (Phase 35 features preserved) ── */}
      <div className="lr2-actbar no-print">
        <div className="lr2-actbar-disclaimer">{T[lang].disclaimer}</div>
        <div className="lr2-actbar-buttons">
          <button onClick={copyKeyPoints} className="lr2-btn ghost">
            {copied ? (lang==="zh"?"✅ 已复制":"✅ Copied") : (lang==="zh"?"📋 复制摘要":"📋 Copy Summary")}
          </button>
          {user && API && (
            <button onClick={()=>setChatOpen(true)} className="lr2-btn primary">
              {lang==="zh"?"💬 追问澄光":"💬 Ask LUMINAARC"}
            </button>
          )}
          {result._form && API && user && (
            <button onClick={downloadProPdf} disabled={proLoad} className="lr2-btn secondary"
              title={lang==="zh"?"澄光能量 · 完整版 PDF":"THE LUMINAARC Pro PDF"}>
              {proLoad ? (lang==="zh"?"生成中…":"Generating…") : (lang==="zh"?"📥 下载完整 PDF":"📥 Download PDF")}
            </button>
          )}
          <button onClick={()=>window.print()} className="lr2-btn ghost">
            {lang==="zh"?"🖨 打印":"🖨 Print"}
          </button>
        </div>
      </div>

      {/* ── ChatDrawer overlay (Phase 35 preserved) ── */}
      {chatOpen && (
        <div className="no-print" onClick={()=>setChatOpen(false)} style={{
          position:"fixed",inset:0,zIndex:9998,
          background:"rgba(20,12,4,.55)",
          backdropFilter:"blur(2px)",
        }}>
          <div onClick={e=>e.stopPropagation()} style={{
            position:"absolute",top:0,right:0,bottom:0,
            width:"min(440px,92vw)",
            background:"linear-gradient(180deg,#1a1208,#0e0a04)",
            borderLeft:"1px solid rgba(212,175,55,.35)",
            display:"flex",flexDirection:"column",
            boxShadow:"-8px 0 40px rgba(0,0,0,.6)",
          }}>
            <div style={{padding:"16px 18px",borderBottom:"1px solid rgba(212,175,55,.22)",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
              <div>
                <div style={{fontSize:14,color:"#C9A84C",fontWeight:600,letterSpacing:.5}}>
                  {lang==="zh"?"☸ 向澄光提问":"☸ Ask LUMINAARC"}
                </div>
                <div style={{fontSize:11,color:"#8B9BB0",marginTop:3}}>{chatQuotaLabel}</div>
              </div>
              <button onClick={()=>setChatOpen(false)} style={{background:"transparent",border:"1px solid rgba(212,175,55,.3)",color:"#C9A84C",fontSize:18,width:32,height:32,borderRadius:6,cursor:"pointer",lineHeight:"1"}}>×</button>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"14px 18px",display:"flex",flexDirection:"column",gap:10}}>
              {chatMessages.length === 0 && (
                <div style={{background:"rgba(212,175,55,.06)",border:"1px solid rgba(212,175,55,.18)",borderRadius:8,padding:"12px 14px",fontSize:13,color:"#A0AEC0",lineHeight:1.6}}>
                  {lang==="zh"
                    ? "你可以基于这份报告深入追问，例如：\n· 我的喜用神为什么是水？\n· 当前大运对事业意味着什么？\n· 第5章的判断如何应用到具体决策？"
                    : "Ask anything about this report, e.g.:\n· Why is my Yong-shen Water?\n· What does the current 大运 mean for career?\n· How to apply the Ch.5 verdicts to a specific decision?"
                  }
                </div>
              )}
              {chatMessages.map((m,i)=>(
                <div key={i} style={{
                  alignSelf: m.role==="user" ? "flex-end" : "flex-start",
                  maxWidth:"86%",
                  background: m.role==="user"
                    ? "linear-gradient(135deg,rgba(212,175,55,.22),rgba(212,175,55,.12))"
                    : (m.isError ? "rgba(180,40,40,.25)" : "rgba(255,255,255,.10)"),
                  border: m.isError ? "1px solid rgba(180,40,40,.5)" : "1px solid rgba(212,175,55,.25)",
                  borderRadius:10,padding:"10px 14px",
                  fontSize:13,color: m.role==="user"?"#C9A84C":"#E2E8F0",
                  whiteSpace:"pre-wrap",lineHeight:1.55,
                }}>
                  {m.content}
                </div>
              ))}
              {chatLoad && (
                <div style={{alignSelf:"flex-start",fontSize:12,color:"#8B9BB0",padding:"4px 6px"}}>
                  {lang==="zh"?"澄光思考中…":"LUMINAARC thinking…"}
                </div>
              )}
            </div>
            <div style={{padding:"12px 14px",borderTop:"1px solid rgba(212,175,55,.22)",display:"flex",gap:8}}>
              <input
                value={chatInput}
                onChange={e=>setChatInput(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); }}}
                placeholder={lang==="zh"?"在此输入问题…":"Type a question…"}
                style={{flex:1,padding:"10px 12px",fontSize:13,background:"rgba(0,0,0,.3)",border:"1px solid rgba(212,175,55,.3)",borderRadius:8,color:"#C9A84C",outline:"none"}}
              />
              <button onClick={sendChat} disabled={chatLoad || !chatInput.trim()}
                style={{background:(chatLoad||!chatInput.trim())?"rgba(212,175,55,.18)":"linear-gradient(135deg,#846B32,#C9A84C)",border:"1px solid rgba(212,175,55,.4)",borderRadius:8,padding:"0 16px",color:"#fff",fontSize:13,fontWeight:600,cursor:(chatLoad||!chatInput.trim())?"not-allowed":"pointer",whiteSpace:"nowrap"}}>
                {lang==="zh"?"发送":"Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Phase 82: 五行→五大 能量翻译 ──────────────────────────────────────
const WUXING_TO_FIVE = {
  "木": { five: "风", tibetan: "rlung", color: "#4A6741", quality: "动、生长、扩展——像春天枝头的新芽，能量向外延展", body: "呼吸、循环、神经系统", imbalance: "风大不调则焦虑、失眠、决策困难", harmonize: "稳定节律、接地活动、暖食" },
  "火": { five: "火", tibetan: "me", color: "#B8453A", quality: "热、转化、光明——像正午的太阳，把一切照得清清楚楚", body: "体温、消化、视力", imbalance: "火大过盛则急躁、炎症、burnout", harmonize: "清凉饮食、冷水浴、避免过度竞争" },
  "土": { five: "地", tibetan: "sa", color: "#8B6914", quality: "坚、承载、稳定——像大地一样厚重可靠", body: "骨骼、肌肉、皮肤", imbalance: "地大不调则沉重、固执、停滞", harmonize: "运动、旅行、简化生活" },
  "金": { five: "空", tibetan: "nam mkha'", color: "#6B7B8D", quality: "空间、容受、清晰——像秋天的晴空，无云无碍", body: "腔体、听觉", imbalance: "空大不调则疏离、空虚无意义感", harmonize: "接地练习、社交连接、具体创造活动" },
  "水": { five: "水", tibetan: "chu", color: "#3A5A7C", quality: "湿、流动、柔韧——像山涧溪流，遇石则绕，终归大海", body: "体液、血液、肾脏", imbalance: "水大不调则恐惧、倦怠、缺乏动力", harmonize: "适度运动、温暖环境、社交激励" },
};

function FiveElementsSection({ dayElem, yongElem, user, lang }) {
  if (!dayElem) return null;
  const dayFE = WUXING_TO_FIVE[dayElem];
  const yongFE = yongElem ? WUXING_TO_FIVE[yongElem] : null;
  if (!dayFE) return null;

  const isZH = lang === "zh";
  const vajraToken = localStorage.getItem("vajra_token");
  const vajraCreds = user?._credentials || [];

  return (
    <div className="lr2-page" id="ch6c">
      <div className="lr2-ph">
        <span className="lr2-ph-num">06C</span>
        <div>
          <div className="lr2-ph-title">{isZH ? "五行→五大 能量翻译" : "Wuxing → Five Elements"}</div>
          <div className="lr2-ph-sub">FIVE ELEMENTS · ENERGY TRANSLATION</div>
        </div>
      </div>

      {/* 日主能量 */}
      <div style={{ marginBottom: 28 }}>
        <div className="lr2-st">{isZH ? `日主${dayElem}→${dayFE.five}大` : `Daymaster ${dayElem}→${dayFE.five}`}</div>
        <div style={{ fontSize: "13.5px", color: "#333", lineHeight: 1.9 }}>
          <p style={{ marginBottom: 10 }}>
            {isZH
              ? `你的日主属${dayElem}，对应密法五大中的「${dayFE.five}大」（藏：${dayFE.tibetan}）。${dayFE.quality}。`
              : `Your daymaster is ${dayElem}, corresponding to the "${dayFE.five}" element (Tib: ${dayFE.tibetan}). ${dayFE.quality}.`}
          </p>
          <p style={{ marginBottom: 10 }}>
            {isZH
              ? `身体对应：${dayFE.body}。${dayFE.imbalance}时，建议：${dayFE.harmonize}。`
              : `Body: ${dayFE.body}. When imbalanced: ${dayFE.imbalance}. Harmonize: ${dayFE.harmonize}.`}
          </p>
          {yongFE && yongFE.five !== dayFE.five && (
            <p style={{ marginBottom: 10 }}>
              {isZH
                ? `你的用神属${yongElem}（${yongFE.five}大），是你最需要补充的能量方向。${yongFE.quality}。`
                : `Your Yong Shen is ${yongElem} (${yongFE.five} element) — the energy you most need. ${yongFE.quality}.`}
            </p>
          )}
        </div>
      </div>

      {/* 修持方向提示 — credential gated */}
      {vajraToken && (
        <div style={{
          borderTop: "1px solid #FBF3DC", paddingTop: 16,
          fontSize: "12.5px", color: "#6B5540", lineHeight: 1.8,
        }}>
          <div className="lr2-st" style={{ fontSize: 10 }}>{isZH ? "金刚社群 · 修持方向" : "Vajra · Practice Direction"}</div>
          <p>{isZH
            ? `你的日主能量对应${dayFE.five}大。在 Vajra Portal 中查看与您能量相应的共修与法会。`
            : `Your daymaster energy aligns with the ${dayFE.five} element. Check Vajra Portal for matching practices and events.`}
          </p>
        </div>
      )}

      <div className="lr2-pf">
        <span className="lr2-pf-label">{isZH ? "澄光能量 · 能量翻译" : "LUMINAARC · Energy Translation"}</span>
      </div>
    </div>
  );
}

export default ReportView;
